import type { ByecodeSize } from '@byecode/ui'
import { Tooltip } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import React, { useEffect, useRef } from 'react'

import { TagIcon } from '../../../TagIcon'
import * as SC from './styles'

interface TextInputProps {
    placeholder?: string
    field?: Field
    data?: string | number
    defaultData?: string | number
    type?: 'number' | 'text'
    width?: string
    disabled?: boolean
    opened?: boolean
    size?: ByecodeSize
    prefix?: React.ReactNode
    focusOutLine?: boolean

    onChange?: React.ChangeEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onUnmountBlur?: (value: string) => void
    onChangeOpen?: () => void
}

export const TextInput: React.FunctionComponent<TextInputProps> = ({
    placeholder,
    data,
    opened,
    size,
    type = 'text',
    width,
    defaultData,
    disabled,
    prefix,
    focusOutLine,
    onChangeOpen,
    onBlur,
    onUnmountBlur,
    onChange
}) => {
    const isFocusedRef = useRef(false)
    const didBlurRef = useRef(false)
    const latestValueRef = useRef<string>(data === undefined ? '' : String(data))

    useEffect(() => {
        latestValueRef.current = data === undefined ? '' : String(data)
    }, [data])

    useEffect(() => {
        return () => {
            if (!didBlurRef.current && isFocusedRef.current) {
                onUnmountBlur?.(latestValueRef.current)
            }
        }
    }, [onUnmountBlur])

    return (
        <SC.ParamsInput
            size={size}
            width={width}
            value={data ?? ''}
            defaultValue={defaultData}
            type={type}
            disabled={disabled}
            placeholder={disabled ? '-' : placeholder}
            prefix={prefix}
            focusOutLine={focusOutLine}
            onChange={e => {
                latestValueRef.current = e.target.value
                onChange?.(e)
            }}
            onKeyDownCapture={e => {
                if (e.key === 'Enter') {
                    e.currentTarget.blur()
                }
            }}
            onFocus={e => {
                isFocusedRef.current = true
                didBlurRef.current = false
            }}
            onBlur={e => {
                isFocusedRef.current = false
                didBlurRef.current = true
                latestValueRef.current = e.target.value
                onBlur?.(e)
            }}
            suffix={
                disabled ? null : (
                    <Tooltip title="选择数据">
                        <TagIcon
                            icon="DotsThreeVertical"
                            size={24}
                            radius={5}
                            isActive={opened}
                            iconSize={16}
                            enableHover
                            hoverBackground="rgba(38, 65, 90, 0.06)"
                            style={{ marginRight: 4 }}
                            onClick={() => onChangeOpen?.()}
                        />
                    </Tooltip>
                )
            }
        />
    )
}
